import type {
  ConfigurationModuleResourceReadBffV2Api,
  ConfigurationModuleResourceReadV2Api,
  ConfigurationModuleResourceWriteV2Api,
  EnumParameterSetFieldValueTypeV2Api,
  EnumSmartFieldDataTypeApi,
  TemplateDetailedResourceReadV2Api,
  TemplateResourceListResponseV2Api,
} from '@ingenico/api-sdk-device-management-private-v2';
import { toArray, toString } from 'lodash-es';
import type { FieldPath } from 'react-hook-form';
import type { TFunction } from 'react-i18next';
import type { SchemaOf } from 'yup';
import { mixed, object, string } from 'yup';
import type { SoftwareInfo } from '../components/module/module-details-content/ModuleDetailsContent.model';
import { mapParameterSetValues } from '../domain/api/mappers/configurationModuleById.mapper';
import type {
  ConfigurationModule,
  ConfigurationModuleEditResource,
  ConfigurationModuleMinimalResourceFront,
  ConfigurationModuleParameterSet,
  ConfigurationModuleParameterSetValue,
  ConfigurationModuleResourceBff,
  ConfigurationModuleSoftwareVersionType,
  TemplateIdsPair,
  TemplateValues,
} from '../domain/models/configurationModules.model';
import type { TemplateDetailedResource } from '../domain/models/templateResource.model';

export function transformBffApiConfigModuleMinimalIntoFrontResource(
  response: ConfigurationModuleResourceReadBffV2Api | ConfigurationModuleResourceReadV2Api,
): Pick<ConfigurationModuleResourceBff, 'parameters'> {
  const parameterSets: (
    | ConfigurationModuleResourceReadBffV2Api
    | ConfigurationModuleResourceReadV2Api
  )['parameterSets'][number][] = [...response.parameterSets];

  return {
    parameters: {
      parameterSets: parameterSets.map(({ templateId, values }) => ({
        values: mapParameterSetValues(values),
        template: { id: templateId },
      })),
    },
  };
}

export function extractTemplateValuesFromConfigurationModulesResponse(
  data?: Pick<ConfigurationModuleResourceBff, 'parameters'> | null,
): TemplateValues {
  const templateValues = data?.parameters.parameterSets;
  return toArray(templateValues);
}

export function extractSoftwareInfoFromConfigurationModulesResponse(data?: ConfigurationModule | null): SoftwareInfo {
  return {
    name: toString(data?.softwareVersion?.softwareName),
    version: toString(data?.softwareVersion?.softwareVersionNumber),
  };
}

export const VALID_INPUT_NAME_MODULE_REGEX = /^[a-zA-Z0-9-. ]{1,255}$/u;

export function createNewModuleSchema(
  getFieldRequiredMessageCallback: (fieldPath?: FieldPath<ConfigurationModuleMinimalResourceFront>) => string,
  t: TFunction,
): SchemaOf<ConfigurationModuleMinimalResourceFront> {
  const requiredMessage = getFieldRequiredMessageCallback();
  return object().shape({
    name: string()
      .transform((value: unknown) => {
        if (typeof value === 'string') {
          const trimmed = value.trim();
          return trimmed === '' ? undefined : trimmed;
        }
        return value;
      })
      .required(requiredMessage)
      .matches(VALID_INPUT_NAME_MODULE_REGEX, t('error.nameValidation'))
      .max(255, t('common:error.maxLength', { maxLength: 255 }))
      .min(1, t('common:error.minLength', { minLength: 1 })),
    description: string()
      .transform((value: unknown) => {
        if (typeof value === 'string') {
          const trimmed = value.trim();
          return trimmed === '' ? undefined : trimmed;
        }
        return value;
      })
      .optional()
      .max(400, t('common:error.maxLength', { maxLength: 400 }))
      .min(1, t('common:error.minLength', { minLength: 1 })),
    softwareType: mixed<ConfigurationModuleSoftwareVersionType>()
      .oneOf(['APPLICATION_VERSION', 'SYSTEM_PACKAGE_VERSION'])
      .required(requiredMessage),
    application: string().required(requiredMessage),
    applicationVersion: string().required(requiredMessage),
  });
}

function transformFieldTypeForRequest(type: EnumSmartFieldDataTypeApi): EnumParameterSetFieldValueTypeV2Api {
  switch (type) {
    case 'STRUCTURE': {
      return 'STRUCTURE';
    }
    default: {
      return 'SINGLE';
    }
  }
}

export function extractParametersFromMultipleTemplatesResponses(
  templateResponses: TemplateDetailedResource[],
): ConfigurationModuleResourceWriteV2Api['parameterSets'] {
  return templateResponses.reduce(
    (accum, current) => [
      ...accum,
      {
        values: current.parameter.fields.map((field) => ({
          fieldId: field.id,
          fieldValue: {
            type: transformFieldTypeForRequest(field.dataType),
          },
        })),
        templateId: toString(current.templateFileId),
      } satisfies ConfigurationModuleResourceWriteV2Api['parameterSets'][number],
    ],
    [] as ConfigurationModuleResourceWriteV2Api['parameterSets'],
  );
}

export function getEditModuleMinimalResources(
  configurationModule?: ConfigurationModule | null,
): ConfigurationModuleEditResource {
  return {
    name: toString(configurationModule?.name),
  };
}

export function filterCurrentVersionTemplatesBasedOnParameterSetAndNewVersionTemplates(
  currentVersionTemplates: TemplateResourceListResponseV2Api,
  newVersionTemplates: TemplateResourceListResponseV2Api,
  parameterSets: ConfigurationModuleParameterSet[],
): TemplateIdsPair[] {
  const filteredCurrentVersionTemplates = currentVersionTemplates.data.filter((template) =>
    parameterSets.find((parameter) => parameter.template.id === template.id),
  );
  return filteredCurrentVersionTemplates.reduce((acc, currentOldTemplate) => {
    const matchingTemplate = newVersionTemplates.data.find(
      (currentNewTemplate) => currentNewTemplate.templateFileId === currentOldTemplate.templateFileId,
    );
    return matchingTemplate
      ? [
          {
            oldIdToMatch: currentOldTemplate.id,
            newId: matchingTemplate.id,
          },
          ...acc,
        ]
      : acc;
  }, [] as TemplateIdsPair[]);
}

export function buildNewParameterSet(
  fetchedTemplates: TemplateDetailedResourceReadV2Api[],
  matchingNewTemplates: TemplateIdsPair[],
  parameterSets: ConfigurationModuleParameterSet[],
): ConfigurationModuleParameterSet[] {
  return fetchedTemplates.reduce((acc, fetchedTemplate) => {
    const oldId = toString(
      matchingNewTemplates.find((templates) => templates.newId === fetchedTemplate.id)?.oldIdToMatch,
    );
    const matchingOldParameter = parameterSets.find((parameter) => parameter.template.id === oldId);
    const builtValues: ConfigurationModuleParameterSetValue[] = toArray(
      matchingOldParameter?.values.filter((value) =>
        fetchedTemplate.parameter.fields.find((newField) => newField.id === value.fieldId),
      ),
    );
    return matchingOldParameter ? [{ template: { id: fetchedTemplate.id }, values: builtValues }, ...acc] : acc;
  }, [] as ConfigurationModuleParameterSet[]);
}
